import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import { SpacingVariants } from "../../../types/spacing";
import classes from "./box.module.css";

export type BorderWidthVariants = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export type BorderStyleVariants = "solid" | "dashed" | "dotted";

export type BorderColorVariants =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quarternary"
  | "positive"
  | "negative";

export type BorderRadiusVariants = "sm" | "md" | "lg";

export type BackgroundColorVariants =
  | "primary"
  | "secondary"
  | "tertiary"
  | "overlay";

export type BoxProps = HTMLAttributes<HTMLDivElement> & {
  paddingY?: SpacingVariants;
  paddingX?: SpacingVariants;
  borderWidth?: BorderWidthVariants;
  borderStyle?: BorderStyleVariants;
  borderColor?: BorderColorVariants;
  borderRadius?: BorderRadiusVariants;
  backgroundColor?: BackgroundColorVariants | string;
  width?: string;
  asChild?: boolean;
};

export const Box = forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      paddingY,
      paddingX,
      borderWidth,
      borderStyle,
      borderColor,
      backgroundColor,
      borderRadius,
      asChild,
      style,
      width,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "div";

    const inlineStyle = {
      ...(paddingY
        ? {
            paddingBlock: `var(--box-padding-y-${paddingY})`,
          }
        : null),
      ...(paddingX
        ? {
            paddingInline: `var(--box-padding-x-${paddingX})`,
          }
        : null),
      ...(borderWidth
        ? { borderWidth: `var(--box-border-width-${borderWidth})` }
        : null),
      ...(borderColor
        ? { borderColor: `var(--box-border-color-${borderColor})` }
        : null),
      ...(borderRadius
        ? { borderRadius: `var(--box-border-radius-${borderRadius})` }
        : null),
      ...(backgroundColor
        ? { backgroundColor: `var(--box-background-${backgroundColor})` }
        : null),
      ...(width ? { width: width } : null),
      ...style,
    };

    return (
      <Comp
        ref={ref}
        className={clsx(
          borderStyle && classes[`border-style-${borderStyle}`],
          className,
        )}
        {...props}
        style={inlineStyle}
      />
    );
  },
);

Box.displayName = "Box";
